import { useState, useEffect } from 'react';
import { useVariance } from '../hooks/useVariance';
import LoadingSpinner  from '../components/LoadingSpinner';
import ErrorCard       from '../components/ErrorCard';

const DEFAULT_THRESHOLD = 15;

function fmt(n) {
  if (!n && n !== 0) return '—';
  const abs = Math.abs(n);
  return (n<0?'-':'') + (abs>=1000?`$${(abs/1000).toFixed(1)}k`:`$${abs.toFixed(0)}`);
}

function AlertCard({ item }) {
  const isIncome    = item.section==='Income'||item.section==='root';
  const pctDisplay  = Math.abs(item.variancePct);
  const severity    = pctDisplay >= 50 ? 'high' : pctDisplay >= 30 ? 'medium' : 'low';
  const borderColor = severity==='high'?'#BE123C':severity==='medium'?'#B45309':'#92400E';
  const bgColor     = severity==='high'?'#FFF1F2':severity==='medium'?'#FFFBEB':'#FFFBEB';

  return (
    <div style={{
      background: bgColor, border: `2px solid ${borderColor}`,
      borderRadius: 8, padding: '1rem',
      display: 'flex', flexDirection: 'column', gap: '0.5rem',
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontWeight:700, color:'#1B2A4A', fontSize:'0.95rem' }}>
            {item.category}
          </div>
          <div style={{ fontSize:'0.75rem', color:'#64748B', marginTop:'0.15rem' }}>
            {isIncome ? '📈 Income' : '📉 Expense'}
          </div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:'1.4rem', fontWeight:800, color:borderColor }}>
            {item.favorable ? '▲' : '▼'} {pctDisplay}%
          </div>
          <div style={{ fontSize:'0.75rem', color:borderColor, fontWeight:600 }}>
            {item.favorable ? 'FAVORABLE' : 'UNFAVORABLE'}
          </div>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0.25rem',
                    borderTop:'1px solid #E2E8F0', paddingTop:'0.5rem' }}>
        {[
          { label:'Budget',   value:fmt(item.budget) },
          { label:'Actual',   value:fmt(item.actual) },
          { label:'Variance', value:(item.favorable?'+':'')+fmt(item.variance) },
        ].map(({label,value}) => (
          <div key={label} style={{ textAlign:'center' }}>
            <div style={{ fontSize:'0.7rem', color:'#94A3B8', marginBottom:'0.1rem' }}>{label}</div>
            <div style={{ fontSize:'0.85rem', fontWeight:600, color:'#1B2A4A' }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Alerts() {

  // Threshold — loads from localStorage, saves on every change
  const [threshold, setThreshold] = useState(() => {
    const saved = localStorage.getItem('acct4cast_alert_threshold');
    return saved ? parseInt(saved) : DEFAULT_THRESHOLD;
  });

  const [history, setHistory] = useState([]);
  const { data, loading, error, refresh } = useVariance();

  // Save threshold to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('acct4cast_alert_threshold', threshold);
  }, [threshold]);

  // Fetch alert history
  useEffect(() => {
    fetch('/api/alerts/history')
      .then(r => r.json())
      .then(d => setHistory(d.history || []))
      .catch(() => {});
  }, [data]);

  // Log current alerts to history when data loads
  useEffect(() => {
    if (!data || !data.variances) return;
    const currentAlerts = data.variances.filter(v =>
      Math.abs(v.variancePct) >= threshold && v.budget !== 0
    );
    if (currentAlerts.length === 0) return;
    fetch('/api/alerts/log', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        alerts:    currentAlerts,
        month:     data.period.month,
        year:      data.period.year,
        threshold,
      }),
    }).catch(() => {});
  }, [data, threshold]);

  if (loading) return <div className="page"><LoadingSpinner message="Checking variance alerts..." /></div>;
  if (error)   return <div className="page"><ErrorCard error={error} onRetry={refresh} /></div>;
  if (!data)   return null;

  const { period, variances } = data;

  const alerts = variances.filter(v =>
    Math.abs(v.variancePct) >= threshold && v.budget !== 0
  );
  const unfavorable = alerts.filter(v => !v.favorable).sort((a,b) => a.variancePct - b.variancePct);
  const favorable   = alerts.filter(v =>  v.favorable).sort((a,b) => b.variancePct - a.variancePct);

  return (
    <div className="page">

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between',
                    alignItems:'center', marginBottom:'1.5rem' }}>
        <div>
          <h2 style={{ marginBottom:'0.25rem' }}>
            Variance Alerts — {period.month} {period.year}
          </h2>
          <p style={{ color:'#64748B', fontSize:'0.85rem' }}>
            {alerts.length} categor{alerts.length===1?'y':'ies'} exceed {threshold}% threshold
          </p>
        </div>
        <button onClick={refresh}
          style={{ padding:'0.4rem 1rem', background:'#0E7C7B', color:'#fff',
                   border:'none', borderRadius:4, cursor:'pointer' }}>
          Refresh
        </button>
      </div>

      {/* Threshold Slider */}
      <div style={{ background:'#fff', borderRadius:8, padding:'1rem 1.5rem',
                    boxShadow:'0 1px 3px rgba(0,0,0,0.08)', marginBottom:'1.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'center', marginBottom:'0.5rem' }}>
          <label style={{ fontWeight:600, color:'#1B2A4A', fontSize:'0.9rem' }}>
            Alert Threshold
          </label>
          <span style={{ fontWeight:700, color:'#0E7C7B', fontSize:'1.1rem' }}>
            {threshold}%
          </span>
        </div>
        <input
          type="range"
          min={5} max={50} step={5}
          value={threshold}
          onChange={e => setThreshold(parseInt(e.target.value))}
          style={{ width:'100%', accentColor:'#0E7C7B', cursor:'pointer' }}
        />
        <div style={{ display:'flex', justifyContent:'space-between',
                      fontSize:'0.75rem', color:'#94A3B8', marginTop:'0.25rem' }}>
          <span>5% — show everything</span>
          <span>50% — worst only</span>
        </div>
        <div style={{ marginTop:'0.5rem', fontSize:'0.8rem', color:'#64748B' }}>
          Showing categories with variance ≥ {threshold}%.
          &nbsp;{alerts.length} of {variances.filter(v=>v.budget!==0).length} categories flagged.
        </div>
      </div>

      {/* No alerts — all clear */}
      {alerts.length === 0 && (
        <div style={{ background:'#DCFCE7', border:'2px solid #15803D',
                      borderRadius:8, padding:'2rem', textAlign:'center' }}>
          <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>✓</div>
          <div style={{ fontWeight:700, color:'#15803D', fontSize:'1.1rem' }}>
            All categories within {threshold}% threshold
          </div>
          <div style={{ color:'#16A34A', fontSize:'0.9rem', marginTop:'0.5rem' }}>
            {period.month} {period.year} is looking clean.
          </div>
        </div>
      )}

      {/* Unfavorable alerts */}
      {unfavorable.length > 0 && (
        <div style={{ marginBottom:'2rem' }}>
          <h3 style={{ color:'#BE123C', marginBottom:'0.75rem', fontSize:'1rem' }}>
            😞 Unfavorable — {unfavorable.length} {unfavorable.length===1?'category':'categories'}
          </h3>
          <div style={{ display:'grid',
                        gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',
                        gap:'1rem' }}>
            {unfavorable.map(item => <AlertCard key={item.category} item={item} />)}
          </div>
        </div>
      )}

      {/* Favorable alerts */}
      {favorable.length > 0 && (
        <div style={{ marginBottom:'2rem' }}>
          <h3 style={{ color:'#15803D', marginBottom:'0.75rem', fontSize:'1rem' }}>
            😊 Favorable — {favorable.length} {favorable.length===1?'category':'categories'}
          </h3>
          <div style={{ display:'grid',
                        gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',
                        gap:'1rem' }}>
            {favorable.map(item => <AlertCard key={item.category} item={item} />)}
          </div>
        </div>
      )}

      {/* Alert History — last 30 days */}
      {history.length > 0 && (
        <div style={{ marginTop:'2rem' }}>
          <h3 style={{ color:'#1B2A4A', marginBottom:'0.75rem', fontSize:'1rem' }}>
            📋 Alert History — Last 30 Days
          </h3>
          <div style={{ background:'#fff', borderRadius:8, overflow:'hidden',
                        boxShadow:'0 1px 3px rgba(0,0,0,0.08)', overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse',
                            fontSize:'0.85rem', minWidth:700 }}>
              <thead>
                <tr style={{ background:'#1B2A4A', color:'#fff' }}>
                  {['Date','Month','Category','Budget','Actual','Variance','%'].map(h => (
                    <th key={h} style={{ padding:'0.6rem 0.75rem',
                                          textAlign: h==='Category'||h==='Date'||h==='Month'
                                            ? 'left' : 'right' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.slice(0,50).map((h, i) => {
                  const vColor = h.favorable ? '#15803D' : '#BE123C';
                  const date   = new Date(h.logged_at).toLocaleDateString();
                  return (
                    <tr key={h.id} style={{ background: i%2===0?'#F8FAFC':'#fff' }}>
                      <td style={{ padding:'0.5rem 0.75rem', color:'#64748B',
                                   fontSize:'0.8rem' }}>{date}</td>
                      <td style={{ padding:'0.5rem 0.75rem', fontSize:'0.8rem' }}>
                        {h.month} {h.year}
                      </td>
                      <td style={{ padding:'0.5rem 0.75rem', fontWeight:500 }}>
                        {h.category}
                      </td>
                      <td style={{ padding:'0.5rem 0.75rem', textAlign:'right' }}>
                        {fmt(h.budget)}
                      </td>
                      <td style={{ padding:'0.5rem 0.75rem', textAlign:'right' }}>
                        {fmt(h.actual)}
                      </td>
                      <td style={{ padding:'0.5rem 0.75rem', textAlign:'right',
                                   color:vColor, fontWeight:600 }}>
                        {fmt(h.variance)}
                      </td>
                      <td style={{ padding:'0.5rem 0.75rem', textAlign:'right',
                                   color:vColor }}>
                        {h.favorable?'+':''}{h.variance_pct}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

export default Alerts;